/* eslint-disable react/display-name */
import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { RootState } from 'Store'
import { Col, Divider, Empty, Row, Space, Tabs } from 'antd'
import { PrimaryButton } from 'Components/button'
import {
  formatDateTime,
  formatDateWithoutTime,
  getCustomerMenuSelect,
  getMenuTypeName,
  getReturnOrderStatus,
  numberWithComma,
} from 'helpers/format'
import { selectCurrentUser } from 'Store/user/selector'
import EventModal from 'Components/modal/EventModal'
import { clearEventAction } from 'Store/supplier-bill/actions'
import { menuReturnPaymentTypes, SupplierInvoiceStatus } from 'helpers/constanst'
import { AppTextArea, SelectItem } from 'Components/form'
import { TableListPage } from 'Components/common/Table'
import { ExpireDate, StatusTag } from 'Components/utilities'
import { getUserLogin } from 'helpers/account'
import { selectAllSaleOrderCustomerList } from 'Store/saleorder-customer/selector'
import { SaleOrderCustomerData, SaleOrderListData } from 'Store/saleorder-customer/types'
import ConfirmWithChild from 'Components/modal/ConfirmWithChild'
import { createSupplierBillAPI } from 'Store/supplier-bill/api'
import { CreateSupplierBillReq } from 'Store/supplier-bill/types'
import { selectSupplierBillActionEvent } from 'Store/supplier-bill/selector'
import { ReturnData } from 'Store/return-order/types'
import SaleOrder from './SaleOrder'
import ReturnSaleOrder from './ReturnSaleOrder'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import { DetailDrawer } from 'Components/common/MainLayout'
import { ShoppingCartOutlined, SwapOutlined } from '@ant-design/icons'

const { TabPane } = Tabs

type Props = ReduxProps & {
  visible: boolean
  onCloseDrawer: () => void
  onSuccess?: () => void
}

const CreateBill: React.FunctionComponent<Props> = props => {
  const { onCloseDrawer, visible, currentUser, event, onSuccess = () => {}, customers } = props

  const dispatch = useDispatch()
  const [selectedSaleOrder, setSelectedSaleOrder] = useState([] as SaleOrderListData[])
  const [activeStatus, setActiveStatus] = useState('order' as string)
  const [customerID, setCustomerID] = useState(0 as number)
  const [showConfirm, setShowConfirm] = useState(false)
  const [selecteCustomer, setSelecteCustomer] = useState(undefined as SaleOrderCustomerData | undefined)
  const [selectedReturnSaleOrder, setSelectedReturnSaleOrder] = useState([] as ReturnData[])
  const [remark, setRemark] = useState('')
  const userInfo = getUserLogin()

  useEffect(() => {
    const sup = customers && customers.find(item => item.id === customerID)
    setSelecteCustomer(sup)
    setSelectedSaleOrder([])
    setSelectedReturnSaleOrder([])
  }, [customerID])

  const getSaleOrderNos = () => {
    const nos = [] as string[]
    selectedSaleOrder &&
      selectedSaleOrder.forEach(item => {
        nos.push(item.sale_order_no)
      })
    return nos
  }

  const getReturnSaleOrderIds = () => {
    const ids = [] as number[]
    selectedReturnSaleOrder &&
      selectedReturnSaleOrder.forEach(item => {
        ids.push(item.id)
      })
    return ids
  }

  const onCreate = (): void => {
    setShowConfirm(false)
    const req: CreateSupplierBillReq = {
      customer_id: selecteCustomer?.id || 0,
      sale_order_nos: getSaleOrderNos(),
      void_ids: getReturnSaleOrderIds(),
      remark: remark,
    }
    dispatch(createSupplierBillAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  useEffect(() => {
    if (visible) {
      setSelectedSaleOrder([])
      setSelectedReturnSaleOrder([])
      setCustomerID(0)
      setShowConfirm(false)
      setActiveStatus('order')
    }
  }, [visible])

  const clearEvent = (): void => {
    dispatch(clearEventAction())
  }

  const onEventSuccess = (): void => {
    setShowConfirm(false)
    clearEvent()
    onSuccess()
  }

  const countTotalPrice = () => {
    let price = 0
    selectedSaleOrder &&
      selectedSaleOrder.forEach(item => {
        price += item.total_remain_amount || 0
      })
    return price
  }

  const countReturnPrice = () => {
    let price = 0
    selectedReturnSaleOrder &&
      selectedReturnSaleOrder.forEach(item => {
        price += item.total_amount || 0
      })
    return price
  }

  const onChangeTab = (key: string) => {
    setActiveStatus(key)
  }

  const renderPaymentDate = (value: string, record: SaleOrderListData) => {
    const isPending =
      record.payment_status === SupplierInvoiceStatus.WAITING ||
      record.payment_status === SupplierInvoiceStatus.PATIAL
    return isPending && value ? (
      <ExpireDate date={value || ''} />
    ) : value ? (
      formatDateWithoutTime(value || '')
    ) : (
      '-'
    )
  }

  const renderInfoItem = (title: string, value: any) => {
    return (
      <Row>
        <Col lg={10} xs={12}>
          <InfoTitle>{title} </InfoTitle>
        </Col>
        <Col lg={14} xs={12}>
          <InfoValue>{value || '-'}</InfoValue>
        </Col>
      </Row>
    )
  }

  const confirmColumns = [
    {
      title: 'เลขที่สั่งซื้อ',
      dataIndex: 'sale_order_no',
      key: 'sale_order_no',
      width: '180px',
    },
    {
      title: 'วันที่สั่งซื้อ',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '200px',
      render: (val: string) => formatDateTime(val),
    },
    {
      title: 'วันที่ครบกำหนดชำระ',
      dataIndex: 'expiry_dt',
      key: 'expiry_dt',
      render: renderPaymentDate,
    },
    {
      title: 'ยอดสั่งซื้อ',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (val: number) => numberWithComma(val, true),
      align: 'right',
    },
    {
      title: 'ยอดชำระ',
      dataIndex: 'total_partial_amount',
      key: 'total_partial_amount',
      render: (val: number) => numberWithComma(val, true),
      align: 'right',
    },
    {
      title: 'ยอดค้างชำระ',
      dataIndex: 'total_remain_amount',
      key: 'total_remain_amount',
      render: (val: number) => <WarningText>{numberWithComma(val, true)}</WarningText>,
      align: 'right',
    },
  ]

  const renderStatus = (status: string) => {
    const statusResult = getReturnOrderStatus(status)
    return <StatusTag text={statusResult.text} color={statusResult.color} />
  }

  const confirmColumnsReturn = [
    {
      title: 'เลขที่สั่งซื้อ',
      dataIndex: 'order_no',
      key: 'order_no',
      width: '180px',
    },
    {
      title: 'วันที่รับคืนสินค้า',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '200px',
      render: (val: string) => formatDateTime(val || ''),
    },
    // {
    //   title: 'ช่องทางการคืนเงิน',
    //   dataIndex: 'return_type',
    //   key: 'return_type',
    //   render: (return_type: string) => getMenuTypeName(menuReturnPaymentTypes, return_type || ''),
    // },
    {
      title: 'ยอดลดหนี้สุทธิ',
      dataIndex: 'total_amount',
      key: 'total_amount',
      align: 'right',
      render: (val: number) => {
        return <WarningText>{numberWithComma(val, true)}</WarningText>
      },
    },
    {
      title: '',
      dataIndex: '',
      key: '',
      width: '30px',
    },
    {
      title: 'สถานะการคืนเงิน',
      dataIndex: 'status',
      key: 'status',
      align: 'right',
      width: '140px',
      render: renderStatus,
    },
  ]

  const orderPrice = countTotalPrice()
  const returnPrice = countReturnPrice()
  const total = orderPrice - returnPrice

  const renderFooterContent = () => {
    return (
      <Row justify="end" align="middle" gutter={[32, 8]}>
        {/* <Col>
          <Space size={'small'} direction="vertical">
            <Space>
              <ShoppingCartOutlined style={{ color: 'green' }} />
              ยอดรวมรายการขาย <GreyText>({selectedSaleOrder.length} รายการ)</GreyText>{' '}
              <GreenText> {numberWithComma(orderPrice, true)} </GreenText>บาท
            </Space>
            <Space>
              <SwapOutlined style={{ color: 'red' }} />
              ยอดรวมรายการคืน <GreyText>({selectedReturnSaleOrder.length} รายการ)</GreyText>{' '}
              <RedText> {numberWithComma(returnPrice, true)} </RedText>บาท
            </Space>
          </Space>
        </Col> */}
        <Col>
          <SumWrapper>
            <Row justify="end" align="middle" gutter={[32, 8]}>
              <Col>
                <Space>
                  รวมเงินทั้งสิ้น <Price>{numberWithComma(total, true)}</Price> บาท
                </Space>
              </Col>
              <Col>
                <PrimaryButton
                  btnType="Primary"
                  text={`สรุปรายการออกใบวางบิล`}
                  onClick={() => setShowConfirm(true)}
                  disabled={selectedSaleOrder.length <= 0}
                />
              </Col>
            </Row>
          </SumWrapper>
        </Col>
      </Row>
    )
  }

  const FilterContent = (
    <FilterWrapper>
      <Row gutter={[16, 8]} align="middle" justify="center">
        <Col>เลือกลูกค้า/คู่ค้า ที่ต้องการออกใบวางบิล</Col>
        <Col xs={24} md={10}>
          <SelectItem
            name="supplier_id"
            label="เลือกลูกค้า/คู่ค้าา ที่ต้องการออกใบวางบิล"
            placeHolder="เลือกลูกค้า/คู่ค้า"
            isRequired={true}
            value={customerID}
            onChange={val => setCustomerID(parseInt(val))}
            menu={getCustomerMenuSelect(customers, 'name', 'id')}
            allowClear={false}
          />
        </Col>
      </Row>
    </FilterWrapper>
  )

  const renderConfirmInfo = () => {
    return (
      <ConfirmWrapper>
        <Row gutter={[8, 8]}>
          <Col md={12} xs={24}>
            <Title>ข้อมูลลูกค้า/คู่ค้า</Title>
            {renderInfoItem('ชื่อลูกค้า/คู่ค้า', selecteCustomer?.name)}
            {renderInfoItem('เบอร์โทรศัพท์', selecteCustomer?.mobile)}
            {renderInfoItem('อีเมล', selecteCustomer?.email)}
            {renderInfoItem('ที่อยู่', selecteCustomer?.address)}
          </Col>
          <Col md={12} xs={24}>
            <Title>ข้อมูลสำหรับออกใบกำกับภาษีรูปแบบเต็ม</Title>
            {renderInfoItem('ชื่อ', selecteCustomer?.tax_name)}
            {renderInfoItem('เลขประจำตัวผู้เสียภาษี', selecteCustomer?.tax_number)}
            {renderInfoItem('เบอร์โทรศัพท์', selecteCustomer?.tax_mobile)}
            {renderInfoItem('ที่อยู่', selecteCustomer?.tax_address)}
          </Col>
          <Divider />
          <Col span={24}>
            <Title>รายการออกใบวางบิล</Title>
          </Col>
          <Col span={24}>
            <TableListPage
              columns={confirmColumns}
              datas={selectedSaleOrder}
              isLoading={false}
              paginationContent={null}
              size="small"
              scrollX={500}
            />
          </Col>
          <Col span={24}>
            <Row justify="space-between" align="middle" gutter={[32, 8]}>
              <Col>
                <Space>จำนวน {numberWithComma(selectedSaleOrder.length)} รายการ</Space>
              </Col>
              <Col>
                <Space>
                  จำนวนเงิน <GreenText>{numberWithComma(orderPrice, true)}</GreenText> บาท
                </Space>
              </Col>
            </Row>
          </Col>
          <Divider />
          <Col span={24}>
            <Title>รายการคืนสินค้า/ลดหนี้</Title>
          </Col>
          <Col span={24}>
            <TableListPage
              columns={confirmColumnsReturn}
              datas={selectedReturnSaleOrder}
              isLoading={false}
              paginationContent={null}
              size="small"
              scrollX={500}
            />
          </Col>
          <Col span={24}>
            <Row justify="space-between" align="middle" gutter={[32, 8]}>
              <Col>
                <Space>จำนวน {numberWithComma(selectedReturnSaleOrder.length)} รายการ</Space>
              </Col>
              <Col>
                <Space>
                  จำนวนเงิน <RedText>{numberWithComma(returnPrice, true)}</RedText> บาท
                </Space>
              </Col>
            </Row>
          </Col>
          <Divider />
          <Col span={24}>
            <SumWrapper>
              <Row justify="end" align="middle" gutter={[32, 8]}>
                <Col span={24}>
                  <Row justify="end" gutter={[32, 8]}>
                    <Col md={8} xs={12}>
                      รวม รายการออกใบวางบิลทั้งหมด
                    </Col>
                    <Col md={4} xs={12} style={{ textAlign: 'right' }}>
                      <GreenText>{numberWithComma(orderPrice, true)}</GreenText> บาท
                    </Col>
                  </Row>
                  <Row justify="end" gutter={[32, 8]}>
                    <Col md={8} xs={12}>
                      หัก รายการคืนสินค้า/ลดหนี้
                    </Col>
                    <Col md={4} xs={12} style={{ textAlign: 'right' }}>
                      <RedText>{numberWithComma(returnPrice, true)}</RedText> บาท
                    </Col>
                  </Row>
                  <Divider />
                  <Row justify="end" gutter={[32, 8]} align="bottom">
                    <Col md={8} xs={12}>
                      รวมเงินทั้งสิ้น
                    </Col>
                    <Col md={4} xs={12} style={{ textAlign: 'right' }}>
                      <Price>{numberWithComma(total, true)} </Price>บาท
                    </Col>
                  </Row>
                </Col>
              </Row>
            </SumWrapper>
          </Col>
          <Divider />
          <Col md={24} xs={24}>
            <AppTextArea
              label="หมายเหตุ"
              placeHolder="ระบุหมายเหตุ"
              isRequired={false}
              isError={remark === ''}
              name="remark"
              rows={2}
              value={remark}
              onChange={val => setRemark(val)}
            />
          </Col>
        </Row>
      </ConfirmWrapper>
    )
  }

  const ConfirmModal = (
    <ConfirmWithChild
      title={'สรุปรายการออกใบวางบิล'}
      isShow={showConfirm}
      handleOk={onCreate}
      handleCancel={() => setShowConfirm(false)}
      child={renderConfirmInfo()}
      cancelBtnTxt="ปิด"
      confirmBtnTxt="ออกใบวางบิล"
      width={'70%'}
    />
  )

  return (
    <DetailDrawer
      title={'ออกใบแจ้งหนี้/ใบวางบิล'}
      onCloseDrawer={onCloseDrawer}
      visible={visible}
      width={'90%'}
      footer={renderFooterContent()}
    >
      <EventModal event={[event]} enable={true} successCalback={onEventSuccess} errorCalback={clearEvent} />
      {FilterContent}
      {customerID <= 0 ? (
        <EmpryWrapper>
          <Empty description="กรุณาเลือก ลูกค้า/คู่ค้า เพื่อออกใบวางบิล" />
        </EmpryWrapper>
      ) : (
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Tabs onChange={onChangeTab} activeKey={activeStatus}>
              <TabPane tab={`รายการขาย`} key={'order'}>
                <SaleOrder
                  customerID={customerID}
                  selectedSaleOrder={selectedSaleOrder}
                  setSelectedSaleOrder={setSelectedSaleOrder}
                  onSuccess={onSuccess}
                />
              </TabPane>
              <TabPane tab={`รายการคืนสินค้า`} key={'order-return'}>
                <ReturnSaleOrder
                  customerID={customerID}
                  selectedSaleOrder={selectedReturnSaleOrder}
                  setSelectedSaleOrder={setSelectedReturnSaleOrder}
                  onSuccess={onSuccess}
                />
              </TabPane>
            </Tabs>
          </Col>
        </Row>
      )}
      {ConfirmModal}
    </DetailDrawer>
  )
}

const Title = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body3};
  font-family: ${(props: { theme: MyTheme }): string => props.theme.fontFamily.bold};
  //color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
`

const FilterWrapper = styled.div`
  padding: 20px 0;
  background-color: ${(props: { theme: MyTheme }): string => props.theme.colors.whitebg};
`

const EmpryWrapper = styled.div`
  padding: 80px 0;
`

const SumWrapper = styled.div`
  background-color: ${(props: { theme: MyTheme }): string => props.theme.colors.whitebg};
  padding: 10px 20px;
  border-radius: 5px;
`

const Price = styled.span`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.primary};
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.title1};
`

const WarningText = styled.div`
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.error};
`

const ConfirmWrapper = styled.div`
  padding: 10px;
  text-align: left;
`

const InfoTitle = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
`

const InfoValue = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.black};
`

const GreenText = styled.span`
  color: green;
`

const RedText = styled.span`
  color: red;
`

const GreyText = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
`

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    event: selectSupplierBillActionEvent(state),
    customers: selectAllSaleOrderCustomerList(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(CreateBill)
